import type { BlockDef, Flow } from '@cogo/types-v2'
import { buildCrossReferenceIndex } from '@/lib/cross-reference'
import { flowNamesMatch } from './flow-ownership'
import {
  buildAppOntology,
  type AppOntology,
  type OntologyEdge,
  type OntologyFlowNode,
  type OntologyUIBinding,
} from './app-ontology'

export type FlowTriggerJudgment =
  | 'page-triggered'
  | 'standalone'
  | 'called-by-flow'
  | 'review-trigger'
  | 'dormant'

export type OntologyTone = 'ok' | 'warn' | 'neutral'
export type FlowOntologyActionId = 'open-flow' | 'bind-selected-page'

export interface FlowOntologyAction {
  id: FlowOntologyActionId
  label: string
  helper: string
  tone: 'primary' | 'secondary' | 'warn'
}

export interface FlowOntologyDecision {
  flowName: string
  blockName: string | null
  trigger: string
  actionSummary: string
  pageTriggerCount: number
  datasourceCount: number
  stateReadCount: number
  stateWriteCount: number
  navigationCount: number
  calledByFlows: string[]
  callsFlows: string[]
  judgment: FlowTriggerJudgment
  label: string
  helper: string
  tone: OntologyTone
  actions: FlowOntologyAction[]
}

export interface ActionFlowOntologyModel {
  ontology: AppOntology
  decisions: FlowOntologyDecision[]
}

const CHANGE_EVENT_ELEMENT_TYPES = new Set([
  'checkbox',
  'combobox',
  'date_picker',
  'input',
  'radio',
  'select',
  'slider',
  'switch',
  'textarea',
  'toggle',
])

export function inferDefaultPageEventType(elementType?: string | null): 'tap' | 'change' {
  const normalized = String(elementType ?? '').trim().toLowerCase()
  return CHANGE_EVENT_ELEMENT_TYPES.has(normalized) ? 'change' : 'tap'
}

export interface ElementOntologyContextChip {
  label: string
  value: string
  tone: OntologyTone
}

export interface ElementOntologyContext {
  elementId: string
  blockName: string
  summary: string
  chips: ElementOntologyContextChip[]
  boundVariables: string[]
  linkedFlows: FlowOntologyDecision[]
  reviewHint: string | null
}

export interface ActionFlowOntologyProject {
  manifest?: {
    pages?: Array<{ id?: string; route?: string }>
  }
  blocks?: Record<string, BlockDef>
  flows?: Record<string, Flow>
}

interface FlowRecord {
  blockName: string | null
  flowName: string
  flow: Flow
}

interface SelectedEventLike {
  flowName: string
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function normalizeFlowStep(step: unknown): unknown {
  if (!isRecord(step)) return step
  const next: Record<string, unknown> = { ...step }
  if (typeof next.action === 'string' && typeof next.type !== 'string') {
    next.type = 'basic'
  }
  if (Array.isArray(next.then)) next.then = next.then.map(normalizeFlowStep)
  if (Array.isArray(next.else)) next.else = next.else.map(normalizeFlowStep)
  if (Array.isArray(next.steps)) next.steps = next.steps.map(normalizeFlowStep)
  if (Array.isArray(next.try)) next.try = next.try.map(normalizeFlowStep)
  if (Array.isArray(next.catch)) next.catch = next.catch.map(normalizeFlowStep)
  if (Array.isArray(next.finally)) next.finally = next.finally.map(normalizeFlowStep)
  if (Array.isArray(next.onEnter)) next.onEnter = next.onEnter.map(normalizeFlowStep)
  if (Array.isArray(next.onSuccess)) next.onSuccess = next.onSuccess.map(normalizeFlowStep)
  if (Array.isArray(next.onError)) next.onError = next.onError.map(normalizeFlowStep)
  if (Array.isArray(next.onFinally)) next.onFinally = next.onFinally.map(normalizeFlowStep)
  if (Array.isArray(next.compensate)) next.compensate = next.compensate.map(normalizeFlowStep)
  if (Array.isArray(next.stages)) next.stages = next.stages.map(normalizeFlowStep)
  if (Array.isArray(next.default)) next.default = next.default.map(normalizeFlowStep)
  if (isRecord(next.cases)) {
    next.cases = Object.fromEntries(
      Object.entries(next.cases).map(([key, value]) => [
        key,
        Array.isArray(value) ? value.map(normalizeFlowStep) : value,
      ]),
    )
  }
  return next
}

function normalizeFlowSteps(steps: unknown): Flow['steps'] {
  return Array.isArray(steps) ? (steps.map(normalizeFlowStep) as Flow['steps']) : []
}

function unwrapFlowMap(value: unknown): Record<string, Flow> {
  if (!isRecord(value)) return {}
  const rawMap = isRecord(value.flows) ? value.flows : value
  const flows: Record<string, Flow> = {}
  for (const [name, maybeFlow] of Object.entries(rawMap)) {
    if (!isRecord(maybeFlow)) continue
    if (!Array.isArray(maybeFlow.steps) && typeof maybeFlow.trigger !== 'string') continue
    flows[name] = {
      ...(maybeFlow as unknown as Flow),
      name: (maybeFlow.name as string | undefined) ?? name,
      trigger: (maybeFlow.trigger as Flow['trigger'] | undefined) ?? 'event',
      steps: normalizeFlowSteps(maybeFlow.steps),
    }
  }
  return flows
}

function collectFlowRecords(project: ActionFlowOntologyProject | null): FlowRecord[] {
  const records: FlowRecord[] = []
  for (const [blockName, block] of Object.entries(project?.blocks ?? {})) {
    const blockFlows = unwrapFlowMap((block as { flow?: unknown }).flow)
    for (const [flowName, flow] of Object.entries(blockFlows)) {
      records.push({ blockName, flowName, flow })
    }
  }
  for (const [flowName, flow] of Object.entries(unwrapFlowMap(project?.flows))) {
    records.push({ blockName: null, flowName, flow })
  }
  return records
}

function collectCrossReferenceFlows(records: FlowRecord[]): Record<string, Flow> {
  const flows: Record<string, Flow> = {}
  for (const record of records) {
    if (!flows[record.flowName]) {
      flows[record.flowName] = record.flow
      continue
    }
    if (record.blockName) {
      flows[`${record.blockName}.${record.flowName}`] = record.flow
    }
  }
  return flows
}

function normalizeBlocksForOntology(blocks: Record<string, BlockDef>): Record<string, BlockDef> {
  const normalized: Record<string, BlockDef> = {}
  for (const [blockName, block] of Object.entries(blocks)) {
    normalized[blockName] = {
      ...block,
      flow: unwrapFlowMap((block as { flow?: unknown }).flow),
    } as BlockDef
  }
  return normalized
}

function projectPages(project: ActionFlowOntologyProject | null): Array<{ id: string; route: string }> {
  return (project?.manifest?.pages ?? []).map((page) => ({
    id: String(page.id ?? page.route ?? 'page'),
    route: String(page.route ?? '/'),
  }))
}

function edgeFlowName(edge: OntologyEdge): string | null {
  if (!edge.source.startsWith('flow:')) return null
  const parts = edge.source.split(':')
  return parts.slice(2).join(':') || null
}

function targetFlowName(edge: OntologyEdge): string | null {
  if (!edge.target.startsWith('flow:')) return null
  const parts = edge.target.split(':')
  return parts.slice(2).join(':') || null
}

function findFlowRecord(records: FlowRecord[], node: OntologyFlowNode): FlowRecord | null {
  return records.find(
    (record) =>
      record.blockName === node.blockName &&
      (flowNamesMatch(record.flowName, node.flowName) || flowNamesMatch(node.flowName, record.flowName)),
  ) ?? null
}

function classifyFlowDecision(input: {
  flow: OntologyFlowNode
  flowRecord: FlowRecord | null
  pageTriggerCount: number
  datasourceCount: number
  stateReadCount: number
  stateWriteCount: number
  navigationCount: number
  calledByFlows: string[]
  callsFlows: string[]
}): Pick<FlowOntologyDecision, 'judgment' | 'label' | 'helper' | 'tone'> {
  const {
    flow,
    flowRecord,
    pageTriggerCount,
    datasourceCount,
    stateReadCount,
    stateWriteCount,
    navigationCount,
    calledByFlows,
    callsFlows,
  } = input
  const trigger = String(flow.trigger ?? flowRecord?.flow.trigger ?? 'event').toLowerCase()
  const stepCount = flowRecord?.flow.steps?.length ?? 0
  const hasResponsibility =
    stepCount > 0 ||
    datasourceCount > 0 ||
    stateReadCount > 0 ||
    stateWriteCount > 0 ||
    navigationCount > 0 ||
    callsFlows.length > 0

  if (pageTriggerCount > 0) {
    return {
      judgment: 'page-triggered',
      label: 'Page trigger',
      helper: `Starts from ${pageTriggerCount} Page event${pageTriggerCount === 1 ? '' : 's'}.`,
      tone: 'ok',
    }
  }
  if (trigger === 'init' || trigger === 'lifecycle' || trigger === 'manual') {
    return {
      judgment: 'standalone',
      label: 'Standalone',
      helper: 'Runs outside a Page click/change. Page binding is optional.',
      tone: 'neutral',
    }
  }
  if (calledByFlows.length > 0) {
    return {
      judgment: 'called-by-flow',
      label: 'Called by flow',
      helper: 'Started by another flow. Bind only if users should start it directly.',
      tone: 'neutral',
    }
  }
  if (!hasResponsibility) {
    return {
      judgment: 'dormant',
      label: 'Dormant',
      helper: 'No Page trigger and no observable responsibility yet.',
      tone: 'warn',
    }
  }
  return {
    judgment: 'review-trigger',
    label: 'Review trigger',
    helper: 'Event flow has work but no Page trigger. Bind it to Page, or call it from another flow.',
    tone: 'warn',
  }
}

function actionsForDecision(decision: Pick<FlowOntologyDecision, 'judgment'>): FlowOntologyAction[] {
  const openFlow: FlowOntologyAction = {
    id: 'open-flow',
    label: 'Open flow',
    helper: 'Open this ActionFlow for inspection.',
    tone: 'secondary',
  }
  if (decision.judgment === 'review-trigger') {
    return [
      {
        id: 'bind-selected-page',
        label: 'Bind selected Page',
        helper: 'Bind the currently selected Page element default event to this flow.',
        tone: 'primary',
      },
      openFlow,
    ]
  }
  if (decision.judgment === 'dormant') {
    return [
      {
        id: 'bind-selected-page',
        label: 'Bind selected Page',
        helper: 'Use this only if a Page element should start the dormant flow.',
        tone: 'warn',
      },
      openFlow,
    ]
  }
  return [openFlow]
}

export function buildActionFlowOntologyModel(project: ActionFlowOntologyProject | null): ActionFlowOntologyModel {
  const blocks = normalizeBlocksForOntology(project?.blocks ?? {})
  const records = collectFlowRecords(project)
  const crossRef = buildCrossReferenceIndex(
    blocks as never,
    collectCrossReferenceFlows(records) as never,
    projectPages(project),
  )
  const ontology = buildAppOntology({
    blocks: blocks as never,
    crossRef,
  })

  const decisions = ontology.flows.map((flow) => {
    const record = findFlowRecord(records, flow)
    const pageTriggers = crossRef.flowUsageIndex.get(flow.flowName) ?? []
    const incoming = ontology.edges.filter((edge) => edge.target === flow.id)
    const outgoing = ontology.edges.filter((edge) => edge.source === flow.id)
    const calledByFlows = incoming
      .filter((edge) => edge.type === 'callFlow')
      .map(edgeFlowName)
      .filter((name): name is string => name !== null)
    const callsFlows = outgoing
      .filter((edge) => edge.type === 'callFlow')
      .map(targetFlowName)
      .filter((name): name is string => name !== null)
    const base = {
      flow,
      flowRecord: record,
      pageTriggerCount: pageTriggers.length,
      datasourceCount: incoming.filter((edge) => edge.type === 'fetch').length,
      stateReadCount: incoming.filter((edge) => edge.type === 'read').length,
      stateWriteCount: outgoing.filter((edge) => edge.type === 'write').length,
      navigationCount: outgoing.filter((edge) => edge.type === 'navigate').length,
      calledByFlows,
      callsFlows,
    }
    const judgment = classifyFlowDecision(base)
    return {
      flowName: flow.flowName,
      blockName: flow.blockName,
      trigger: flow.trigger,
      actionSummary: flow.actionSummary,
      pageTriggerCount: base.pageTriggerCount,
      datasourceCount: base.datasourceCount,
      stateReadCount: base.stateReadCount,
      stateWriteCount: base.stateWriteCount,
      navigationCount: base.navigationCount,
      calledByFlows,
      callsFlows,
      ...judgment,
      actions: actionsForDecision(judgment),
    }
  })

  return { ontology, decisions }
}

export function findFlowOntologyDecision(
  model: ActionFlowOntologyModel,
  flowName: string,
): FlowOntologyDecision | null {
  return model.decisions.find(
    (decision) =>
      flowNamesMatch(decision.flowName, flowName) ||
      flowNamesMatch(flowName, decision.flowName),
  ) ?? null
}

export function fallbackFlowOntologyDecision(flow: {
  name: string
  trigger?: string | null
}): FlowOntologyDecision {
  const trigger = String(flow.trigger ?? 'event').toLowerCase()
  const base = {
    flowName: flow.name,
    blockName: null,
    trigger,
    actionSummary: 'editor-only',
    pageTriggerCount: 0,
    datasourceCount: 0,
    stateReadCount: 0,
    stateWriteCount: 0,
    navigationCount: 0,
    calledByFlows: [],
    callsFlows: [],
  }
  if (trigger === 'init' || trigger === 'lifecycle' || trigger === 'manual') {
    return {
      ...base,
      judgment: 'standalone',
      label: 'Standalone',
      helper: 'Runs outside a Page click/change. Page binding is optional.',
      tone: 'neutral',
      actions: actionsForDecision({ judgment: 'standalone' }),
    }
  }
  return {
    ...base,
    judgment: 'review-trigger',
    label: 'Review trigger',
    helper: 'Editor-only event flow has no Page trigger yet.',
    tone: 'warn',
    actions: actionsForDecision({ judgment: 'review-trigger' }),
  }
}

export function getElementOntologyContext({
  model,
  blockName,
  elementId,
  selectedEvents,
}: {
  model: ActionFlowOntologyModel
  blockName: string
  elementId: string
  selectedEvents: SelectedEventLike[]
}): ElementOntologyContext {
  const uiBindings = model.ontology.uiBindings.filter(
    (binding) => binding.blockName === blockName && binding.elementId === elementId,
  )
  const uiBindingIds = new Set(uiBindings.map((binding) => binding.id))
  const bindEdges = model.ontology.edges.filter(
    (edge) => edge.type === 'bind' && uiBindingIds.has(edge.target),
  )
  const boundStateIds = new Set(bindEdges.map((edge) => edge.source))
  const boundStates = model.ontology.states.filter((state) => boundStateIds.has(state.id))
  const linkedFlows = selectedEvents
    .map((event) => findFlowOntologyDecision(model, event.flowName))
    .filter((decision): decision is FlowOntologyDecision => decision !== null)
  const stateWriteCount = linkedFlows.reduce((sum, flow) => sum + flow.stateWriteCount, 0)
  const reviewFlow = linkedFlows.find(
    (flow) => flow.judgment === 'review-trigger' || flow.judgment === 'dormant',
  )

  const chips: ElementOntologyContextChip[] = []
  chips.push({
    label: 'Page triggers',
    value: String(linkedFlows.reduce((sum, flow) => sum + flow.pageTriggerCount, 0)),
    tone: linkedFlows.length > 0 ? 'ok' : 'neutral',
  })
  chips.push({
    label: 'Data shown',
    value: String(uiBindings.length),
    tone: uiBindings.length > 0 ? 'ok' : 'neutral',
  })
  if (stateWriteCount > 0) {
    chips.push({ label: 'State writes', value: String(stateWriteCount), tone: 'warn' })
  }
  if (boundStates.length > 0) {
    chips.push({
      label: 'State source',
      value: String(boundStates.length),
      tone: 'neutral',
    })
  }

  const responsibilities = [
    linkedFlows.length > 0 ? 'interactive' : null,
    uiBindings.length > 0 ? 'data-bound' : null,
    stateWriteCount > 0 ? 'state-changing' : null,
  ].filter(Boolean)

  return {
    elementId,
    blockName,
    summary: responsibilities.length > 0 ? responsibilities.join(' + ') : 'static element',
    chips,
    boundVariables: Array.from(
      new Set(uiBindings.map((binding: OntologyUIBinding) => binding.boundVariable)),
    ),
    linkedFlows,
    reviewHint: reviewFlow?.helper ?? null,
  }
}
